// src/pages/ResetCodePage.jsx
// ============================================================
// Paso 2 de 3: ingresar el código que llegó por mail.
//
// El código NO se pide al montar: ya lo mandó ForgotPasswordPage. Volver a
// pedirlo gastaría uno de los 3 envíos cada 15 minutos que permite el backend
// y encima invalidaría el código que el usuario está por tipear, porque cada
// /send pisa el anterior.
//
// El código se canjea por un TICKET de un solo uso y muere en ese momento.
//
// ⚠ Sobre el interceptor de 401 de src/api/api.js: ver la nota extensa en
//    ForgotPasswordPage.jsx. Resumen: hoy no aplica porque estos endpoints
//    devuelven 400 y nunca 401; si eso cambiara, el usuario sería expulsado
//    al inicio en lugar de ver el error.
// ============================================================

import { useState } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { useNavigate, useLocation, Navigate, Link as RouterLink } from 'react-router-dom';
import api from '../api/api';
import AuthCard from '../components/AuthCard';
import OtpInput, { otpVacio, OTP_LARGO } from '../components/OtpInput';

export default function ResetCodePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  const [code, setCode]             = useState(otpVacio());
  const [loading, setLoading]       = useState(false);
  const [reenviando, setReenviando] = useState(false);
  const [error, setError]           = useState('');
  const [aviso, setAviso]           = useState('');

  // En mobile no hacía falta —no se puede tipear una ruta— pero acá alguien
  // puede entrar directo a /recuperar/codigo. Sin email no hay nada que hacer.
  // <Navigate> y no navigate() en el cuerpo: navegar durante el render es un
  // efecto lateral y React avisa.
  if (!email) return <Navigate to="/recuperar" replace />;

  const verificar = async (codigoCompleto) => {
    const full = codigoCompleto ?? code.join('');
    if (full.length < OTP_LARGO) {
      setError('Ingresá el código completo');
      return;
    }

    setLoading(true);
    setError('');
    setAviso('');
    try {
      const r = await api.post('/auth/password-reset/verify', { email, code: full });
      navigate('/recuperar/clave', { state: { email, ticket: r.data.ticket } });
    } catch (e) {
      // El backend usa el MISMO mensaje para "no hay solicitud", "venció" y
      // "código incorrecto", para no decir si esa cuenta pidió un reset. Se
      // muestra tal cual: distinguirlos acá anularía esa decisión.
      setError(e.response?.data?.error || 'Código inválido o vencido.');
      setCode(otpVacio());
    } finally {
      setLoading(false);
    }
  };

  const reenviar = async () => {
    setReenviando(true);
    setError('');
    setAviso('');
    try {
      await api.post('/auth/password-reset/send', { email });
      setCode(otpVacio());
      setAviso('Te mandamos un código nuevo. El anterior ya no sirve.');
    } catch (e) {
      // Acá cae el límite de 3 envíos cada 15 minutos, con su propio texto.
      setError(e.response?.data?.error || 'No pudimos reenviar el código.');
    } finally {
      setReenviando(false);
    }
  };

  return (
    <AuthCard
      titulo="Ingresá el código"
      subtitulo={`Te lo mandamos a ${email}`}
    >
      <OtpInput value={code} onChange={setCode} onComplete={verificar} disabled={loading} />

      {error && (
        <Typography sx={{ fontSize: 13, color: '#f87171', textAlign: 'center', mt: 1, mb: 1 }}>
          {error}
        </Typography>
      )}
      {aviso && (
        <Typography sx={{ fontSize: 13, color: '#E7A432', textAlign: 'center', mt: 1, mb: 1 }}>
          {aviso}
        </Typography>
      )}

      <Button
        fullWidth
        onClick={() => verificar()}
        disabled={loading}
        sx={{
          mt: 2, bgcolor: '#E7A432', color: '#163244', borderRadius: 2, py: 1.4,
          fontWeight: 700, fontSize: 15,
          '&:hover': { bgcolor: '#f5bc55' },
        }}
      >
        {loading ? <CircularProgress size={22} sx={{ color: '#163244' }} /> : 'Continuar'}
      </Button>

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Typography
          onClick={reenviando || loading ? undefined : reenviar}
          sx={{
            fontSize: 14, color: 'rgba(255,255,255,0.65)',
            cursor: reenviando || loading ? 'default' : 'pointer',
          }}
        >
          ¿No te llegó? <Box component="span" sx={{ color: '#E7A432', fontWeight: 600 }}>Reenviar</Box>
        </Typography>

        <Typography
          component={RouterLink}
          to="/"
          sx={{ display: 'block', fontSize: 14, color: 'rgba(255,255,255,0.65)', textDecoration: 'none', mt: 1 }}
        >
          Volver a <Box component="span" sx={{ color: '#E7A432', fontWeight: 600 }}>iniciar sesión</Box>
        </Typography>
      </Box>
    </AuthCard>
  );
}
